/**
 * Provider factory: ritorna l'implementazione `WhatsAppProvider` corretta in base
 * al `service_name` di `api_configurations`.
 *
 * - 'waha'                → WahaProvider (singleton, wraps wahaApiService)
 * - 'whatsapp_cloud'      → CloudProvider (Standalone, no Coex flag)
 * - 'whatsapp_cloud_coex' → CloudProvider (Coex flag attivo, capabilities ridotte)
 *
 * Pattern di uso:
 *   const provider = getProvider({ serviceName, configId, organizationId,
 *                                  phoneNumberId, wabaId });
 *   await provider.sendText(chatId, "ciao");
 */

import type { WhatsAppProvider, WhatsAppProviderType } from './types';
import { WahaProvider } from './WahaProvider';
import { CloudProvider } from './CloudProvider';

// Singleton WahaProvider — wahaApiService dietro è già singleton, OK riusare istanza.
let wahaSingleton: WahaProvider | null = null;

export interface ProviderResolveOptions {
  serviceName: string;
  /** Richiesti per CloudProvider; ignorati per WAHA. */
  configId?: string;
  organizationId?: string;
  phoneNumberId?: string;
  wabaId?: string;
}

export function getProvider(opts: ProviderResolveOptions | string): WhatsAppProvider {
  const o: ProviderResolveOptions = typeof opts === 'string' ? { serviceName: opts } : opts;
  const type = normalizeServiceName(o.serviceName);

  switch (type) {
    case 'whatsapp':
      if (!wahaSingleton) wahaSingleton = new WahaProvider();
      return wahaSingleton;

    case 'whatsapp_cloud':
    case 'whatsapp_cloud_coex': {
      if (!o.configId || !o.organizationId || !o.phoneNumberId || !o.wabaId) {
        throw new Error(
          'CloudProvider richiede configId, organizationId, phoneNumberId, wabaId. ' +
            'Carica la config da api_configurations prima di chiamare getProvider.',
        );
      }
      return new CloudProvider({
        configId: o.configId,
        organizationId: o.organizationId,
        phoneNumberId: o.phoneNumberId,
        wabaId: o.wabaId,
        isCoex: type === 'whatsapp_cloud_coex',
      });
    }

    default:
      throw new Error(`service_name non riconosciuto come provider WhatsApp: "${o.serviceName}"`);
  }
}

/**
 * Verifica se un `service_name` è gestito dal provider abstraction WhatsApp.
 * Utile per filtrare le config WhatsApp da altre (gmail, voxloud, ecc.).
 */
export function isWhatsAppServiceName(serviceName: string | null | undefined): boolean {
  if (!serviceName) return false;
  return ['waha', 'whatsapp_cloud', 'whatsapp_cloud_coex'].includes(serviceName);
}

function normalizeServiceName(serviceName: string): WhatsAppProviderType {
  switch (serviceName) {
    case 'waha':
    case 'whatsapp':
      return 'whatsapp';
    case 'whatsapp_cloud':
      return 'whatsapp_cloud';
    case 'whatsapp_cloud_coex':
      return 'whatsapp_cloud_coex';
    default:
      // Mai fallback silenzioso: meglio errore esplicito per debug.
      throw new Error(`service_name "${serviceName}" non mappato a un provider WhatsApp`);
  }
}
